import RequestToken from '../../../../tokens/RequestToken';
import {CommandRequester} from '../../../../CommandRequester';
import RequestAPI from '../../../RequestAPI';
import {Event} from '../../../../events/Event';
import {CommandTypes, EntityTrackEvents, StreamTypes} from '@jibo/command-protocol';

/**
 * @class FaceTrackToken
 * @extends RequestToken
 * @hideconstructor
 */
export class FaceTrackToken extends RequestToken<JIBO.v1.EntityRequest, never> {
    /**
     * Update on location of face being tracked.
     * @name FaceTrackToken#update
     * @type {Event}
     */
    public update:Event<JIBO.v1.TrackedEntity[]>;
    /**
     * New face being tracked.
     * @name FaceTrackToken#gained
     * @type {Event}
     */
    public gained:Event<JIBO.v1.TrackedEntity[]>;
    /**
     * Currently tracked face was lost.
     * @name FaceTrackToken#lost
     * @type {Event}
     */
    public lost:Event<JIBO.v1.TrackedEntity[]>;

    constructor(owner:CommandRequester, protocol: JIBO.v1.EntityRequest) {
        super(owner, protocol);
        this.update = new Event('Update of tracked entities');
        this.gained = new Event('Entity found');
        this.lost = new Event('Entity lost');
    }

    /**
     * @private
     */
    public handleAck(ack:JIBO.v1.Acknowledgement) {
        //handle ack
        if (ack.Response.ResponseCode >= 400) {
            this.isComplete = true;
            this._complete.reject(ack.Response);
            return;
        }
    }

    /**
     * @private
     */
    public handleEvent(event:JIBO.v1.EventMessage) {
        //handle response
        const message = event.EventBody;
        switch (message.Event) {
            case EntityTrackEvents.TrackGained:
                this.gained.emit(message.Tracks);
                break;
            case EntityTrackEvents.TrackUpdate:
                this.update.emit(message.Tracks);
                break;
            case EntityTrackEvents.TrackLost:
                this.lost.emit(message.Tracks);
                break;
        }
    }
}

/**
 * Internal FaceTrack class
 * @class CommandRequester.perception.subscribe.FaceTrack
 * @intdocs
 */
export class FaceTrack extends RequestAPI<JIBO.v1.EntityRequest, FaceTrackToken> {

    /**
     * Generate Face Track Protocol
     * @method CommandRequester.perception.subscribe.FaceTrack#generateProtocol
     * @returns {EntityRequest}
     * @intdocs
     */
    static generateProtocol(): JIBO.v1.EntityRequest {
        return {
            Type: CommandTypes.Subscribe,
            StreamType: StreamTypes.Entity,
            StreamFilter: {}
        };
    }

    /**
     * Create FaceTrackToken from EntityRequest protocol.
     * @method CommandRequester.perception.subscribe.FaceTrack#generateToken
     * @param {EntityRequest} protocol - EntityRequest protocol to generate a FaceTrackToken from.
     * @param {boolean} [andSend=false] - `true` if the generated FaceTrackToken should also be sent as a request.
     * @return {FaceTrackToken}
     * @intdocs
     */
    public generateToken(protocol: JIBO.v1.EntityRequest, andSend=false): FaceTrackToken {
        const token = new FaceTrackToken(this.owner, protocol);
        if (andSend) {
            super.sendToken(token);
        }
        return token;
    }

    /**
     * Send FaceTrackToken request.
     * <p>NOTE: Implementation in base class {@link RequestAPI}</p>
     * @method CommandRequester.perception.subscribe.FaceTrack#sendToken
     * @param {FaceTrackToken} token - FaceTrackToken to send.
     * @intdocs
     */
}